import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import axios from "axios";

import Images from "../utils/Images";
import { API } from "../utils/Api";

import CartDrawer, { clearLocalCart } from "../components/CartDrawer";
import ProductDrawer from "../components/ProductDrawer";
import Header from "../components/Header";
import ProductCard from "../components/ProductCard";
import AdvertisementCarousel from "../components/AdvertisementCarousel";
import ProductFilterPopover from "../components/ProductFilterPopover";
import CategoryItem from "../components/CategoryItem";
import TrackedOrders from "../components/TrackedOrders";

const MENU_EASE = [0.22, 1, 0.36, 1];

const SEARCH_PLACEHOLDER_INTERVAL_MS = 3000;

const SEARCH_PLACEHOLDERS = [
  { emoji: "🥶", text: "Cold Coffee?" },
  { emoji: "🧋", text: "Iced Latte?" },
  { emoji: "🥐", text: "Butter Croissant?" },
  { emoji: "🥗", text: "Fresh Salad?" },
  { emoji: "🍕", text: "Cheese Pizza?" },
  { emoji: "🥪", text: "Club Sandwich?" },
  { emoji: "🍩", text: "Glazed Donut?" },
  { emoji: "🍦", text: "Ice Cream?" },
];

const CATEGORY_STICKY_NAME_HEIGHT = 46;

function CategoryStickyName({
  name,
  stickyTop = 0,
  coveredByBanner = false,
}) {
  return (
    <Box
      sx={{
        position: "sticky",
        top: stickyTop,
        zIndex: 2,
        backgroundColor: "#DFEEF5",
      }}
    >
      <Box
        sx={{
          height: CATEGORY_STICKY_NAME_HEIGHT,
          px: 2.2,
          display: "flex",
          position: "relative",
          top: -1,
          alignItems: "center",
          gap: 1,
          backgroundColor: coveredByBanner ? "#FFFFFF" : "#09A5F7",
          background: coveredByBanner
            ? "#FFFFFF"
            : "linear-gradient(90deg, #09A5F7 0%, #57C7FF 100%)",
          border: "none",
          overflow: "hidden",
          borderTopLeftRadius: "22px",
          borderTopRightRadius: "22px",
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: coveredByBanner ? 14.5 : 18,
            fontWeight: 800,
            color: coveredByBanner ? "#12364D" : "#FFFFFF",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </Typography>
      </Box>
    </Box>
  );
}

const menuFadeUp = (
  delay = 0,
  prefersReducedMotion = false,
  canAnimate = false,
) => {
  const hidden = { opacity: 0, y: 18 };
  const visible = { opacity: 1, y: 0 };

  if (prefersReducedMotion) {
    return {
      initial: false,
      animate: visible,
      transition: { duration: 0 },
    };
  }

  return {
    initial: hidden,
    animate: canAnimate ? visible : hidden,
    transition: {
      duration: 0.48,
      delay: canAnimate ? delay : 0,
      ease: MENU_EASE,
    },
  };
};

const resolveOutletId = (outletId) =>
  outletId || localStorage.getItem("outletId");

const menuSessionCache = {
  outletId: null,
  categories: [],
  outletData: null,
};

const getCachedMenu = (outletId) => {
  if (!outletId || menuSessionCache.outletId !== outletId) {
    return null;
  }

  return menuSessionCache;
};

const setCachedMenu = (outletId, { categories, outletData } = {}) => {
  if (!outletId) {
    return;
  }

  menuSessionCache.outletId = outletId;

  if (categories !== undefined) {
    menuSessionCache.categories = categories;
  }

  if (outletData !== undefined) {
    menuSessionCache.outletData = outletData;
  }
};

const clearCachedMenu = (outletId) => {
  if (!outletId || menuSessionCache.outletId === outletId) {
    menuSessionCache.outletId = null;
    menuSessionCache.categories = [];
    menuSessionCache.outletData = null;
  }
};

const isEventCategoryItem = (item) => Boolean(item?.isEventCategory);

const excludeEventMenuItems = (categoriesList = []) =>
  (Array.isArray(categoriesList) ? categoriesList : [])
    .filter((category) => !isEventCategoryItem(category))
    .map((category) => ({
      ...category,
      products: Array.isArray(category?.products)
        ? category.products.filter((product) => !isEventCategoryItem(product))
        : [],
    }));

const syncOutletData = (outletId) => {
  if (!outletId) {
    return null;
  }

  const previousOutletId = localStorage.getItem("outletId");

  if (previousOutletId && previousOutletId !== outletId) {
    clearLocalCart();
  }

  localStorage.setItem("outletId", outletId);

  try {
    const stored = JSON.parse(localStorage.getItem("outletData")) || null;

    if (stored?._id === outletId) {
      return stored;
    }
  } catch (error) {
    console.error("Invalid outletData:", error);
  }

  const nextOutlet = { _id: outletId };
  localStorage.setItem("outletData", JSON.stringify(nextOutlet));

  return nextOutlet;
};

function Menu({ splashComplete = true }) {
  const navigate = useNavigate();

  const { outletId } = useParams();

  const prefersReducedMotion = useReducedMotion();

  const cartDrawerRef = useRef(null);

  const categoryContainerRef = useRef(null);

  const stickyHeaderRef = useRef(null);

  const [remainingCategoryCount, setRemainingCategoryCount] = useState(0);

  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  const cachedMenu = getCachedMenu(resolveOutletId(outletId));

  const [outletData, setOutletData] = useState(
    () => cachedMenu?.outletData || syncOutletData(resolveOutletId(outletId)),
  );

  const [categories, setCategories] = useState(() =>
    excludeEventMenuItems(cachedMenu?.categories || []),
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [productDrawerOpen, setProductDrawerOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  const [searchPlaceholderIndex, setSearchPlaceholderIndex] = useState(0);

  const currentSearchPlaceholder =
    SEARCH_PLACEHOLDERS[searchPlaceholderIndex] || SEARCH_PLACEHOLDERS[0];

  const [dietaryFilter, setDietaryFilter] = useState("ALL");

  const [sortOption, setSortOption] = useState("DEFAULT");

  const [loading, setLoading] = useState(
    () => !(cachedMenu?.categories?.length > 0),
  );

  const canAnimate = splashComplete && !loading;

  const [message, setMessage] = useState({
    open: false,
    text: "",
    severity: "success",
  });

  const [cart, setCart] = useState([]);

  const productQuantities = useMemo(() => {
    const quantities = {};

    cart.forEach((cartItem) => {
      const productId = cartItem?.productId;

      if (!productId) {
        return;
      }

      quantities[productId] =
        (quantities[productId] || 0) + Number(cartItem?.quantity || 0);
    });

    return quantities;
  }, [cart]);

  const handleCartChange = useCallback((updatedCart) => {
    setCart(Array.isArray(updatedCart) ? updatedCart : []);
  }, []);

  const showMessage = (text, severity = "success") => {
    setMessage({
      open: true,
      text,
      severity,
    });
  };

  const getProductPrice = (product) => {
    return Number(
      product?.newOfferPrice?.toFixed(0) || product?.price?.toFixed(0),
    );
  };

  const getProductImage = (product) => {
    return (
      product?.displayImageUrl ||
      product?.displayImage ||
      product?.imageUrl ||
      product?.image ||
      Images.product
    );
  };

  const getProductDescription = (product) => {
    return (
      product?.shortDescription ||
      product?.description ||
      product?.category?.name ||
      "Freshly prepared for you"
    );
  };

  const getPreparationTime = (product) => {
    const preparationTime = product?.makingTime || 20;
    return Number(preparationTime) || 20;
  };

  const getProductDietaryType = (product) => {
    /*
     * Add or modify the fields below
     * according to your actual API response.
     */
    const rawType =
      product?.foodType ||
      product?.dietaryType ||
      product?.dietaryPreference ||
      product?.productType ||
      product?.mealType ||
      product?.type ||
      product?.foodCategory ||
      "";

    const normalizedType = String(rawType)
      .trim()
      .toLowerCase()
      .replace(/[\s_-]/g, "");

    if (
      product?.isVegan === true ||
      normalizedType === "vegan" ||
      normalizedType.includes("vegan")
    ) {
      return "VEGAN";
    }

    if (
      product?.isNonVeg === true ||
      product?.isNonVegetarian === true ||
      normalizedType === "nonveg" ||
      normalizedType === "nonvegetarian" ||
      normalizedType.includes("nonveg")
    ) {
      return "NON_VEG";
    }

    if (
      product?.isVeg === true ||
      product?.isVegetarian === true ||
      normalizedType === "veg" ||
      normalizedType === "vegetarian"
    ) {
      return "VEG";
    }

    return "OTHER";
  };

  const sortProducts = (products) => {
    const sortedProducts = [...products];

    switch (sortOption) {
      case "PRICE_LOW_HIGH":
        return sortedProducts.sort(
          (firstProduct, secondProduct) =>
            getProductPrice(firstProduct) - getProductPrice(secondProduct),
        );

      case "PRICE_HIGH_LOW":
        return sortedProducts.sort(
          (firstProduct, secondProduct) =>
            getProductPrice(secondProduct) - getProductPrice(firstProduct),
        );

      case "NAME_ASC":
        return sortedProducts.sort((firstProduct, secondProduct) =>
          String(firstProduct?.name || "").localeCompare(
            String(secondProduct?.name || ""),
            undefined,
            {
              sensitivity: "base",
            },
          ),
        );

      case "PREPARATION_TIME":
        return sortedProducts.sort(
          (firstProduct, secondProduct) =>
            getPreparationTime(firstProduct) -
            getPreparationTime(secondProduct),
        );

      default:
        return sortedProducts;
    }
  };

  const clearFilters = () => {
    setDietaryFilter("ALL");
    setSortOption("DEFAULT");
  };

  const getCategoriesProductsList = async () => {
    if (!outletData?._id) {
      return;
    }

    const hasCachedProducts =
      menuSessionCache.outletId === outletData._id &&
      menuSessionCache.categories.length > 0;

    try {
      if (!hasCachedProducts) {
        setLoading(true);
      }

      const response = await axios.get(
        `${API.BASE_URL}product/getProductsWithAddonsv4?outletId=${outletData._id}&orderType=PICKUP`,
      );

      const responseData = response?.data?.data || response?.data || {};

      let fetchedCategories = responseData?.categories || [];

      if (!Array.isArray(fetchedCategories)) {
        fetchedCategories = [];
      }

      const menuGroupId =
        responseData?.menuGroup?._id ||
        responseData?.menuGroup ||
        responseData?.menuGroupId ||
        fetchedCategories
          .flatMap((category) =>
            Array.isArray(category?.products) ? category.products : [],
          )
          .map(
            (product) =>
              product?.menuGroup?._id ||
              product?.menuGroup ||
              product?.menuGroupId,
          )
          .find(Boolean) ||
        null;

      if (menuGroupId && outletData?._id) {
        const nextOutletData = {
          ...outletData,
          menuGroup: menuGroupId,
        };

        setOutletData(nextOutletData);
        localStorage.setItem("outletData", JSON.stringify(nextOutletData));
        setCachedMenu(outletData._id, {
          outletData: nextOutletData,
        });
      }

      fetchedCategories = excludeEventMenuItems(fetchedCategories);

      setCategories(fetchedCategories);
      setCachedMenu(outletData._id, {
        categories: fetchedCategories,
        outletData: menuGroupId
          ? {
              ...outletData,
              menuGroup: menuGroupId,
            }
          : outletData,
      });
    } catch (error) {
      console.error("Error fetching products:", error?.response?.data || error);

      showMessage(
        error?.response?.data?.message || "Unable to fetch products.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const visibleCategories = useMemo(
    () => excludeEventMenuItems(categories),
    [categories],
  );

  const visibleCategorySections = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    const activeCategoryId = visibleCategories.some(
      (category) => category?._id === selectedCategoryId,
    )
      ? selectedCategoryId
      : null;

    return visibleCategories
      .map((category) => {
        let categoryProducts = Array.isArray(category?.products)
          ? category.products.map((product) => ({
              ...product,
              category: {
                _id: category?._id,
                name: category?.name,
              },
            }))
          : [];

        if (activeCategoryId) {
          categoryProducts =
            category?._id === activeCategoryId ? categoryProducts : [];
        }

        if (normalizedSearch) {
          categoryProducts = categoryProducts.filter((product) => {
            const productName = String(product?.name || "").toLowerCase();

            const description = String(
              getProductDescription(product),
            ).toLowerCase();

            return (
              productName.includes(normalizedSearch) ||
              description.includes(normalizedSearch)
            );
          });
        }

        if (dietaryFilter !== "ALL") {
          categoryProducts = categoryProducts.filter(
            (product) => getProductDietaryType(product) === dietaryFilter,
          );
        }

        categoryProducts = sortProducts(categoryProducts);

        return {
          ...category,
          products: categoryProducts,
        };
      })
      .filter((category) => category.products.length > 0);
  }, [
    visibleCategories,
    selectedCategoryId,
    searchQuery,
    dietaryFilter,
    sortOption,
  ]);

  const updateRemainingCategoryCount = useCallback(() => {
    const container = categoryContainerRef.current;

    if (!container) {
      setRemainingCategoryCount(0);

      return;
    }

    const containerRect = container.getBoundingClientRect();
    const categoryItems = Array.from(container.children);
    const badgeOverlap = 34;

    const remaining = categoryItems.filter((child) => {
      const childRect = child.getBoundingClientRect();

      return childRect.left >= containerRect.right - badgeOverlap;
    }).length;

    setRemainingCategoryCount(remaining);
  }, []);

  const scrollToMoreCategories = () => {
    const container = categoryContainerRef.current;

    if (!container) {
      return;
    }

    container.scrollBy({
      left: Math.max(container.clientWidth * 0.7, 220),
      behavior: "smooth",
    });
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategoryId((currentSelectedCategoryId) =>
      currentSelectedCategoryId === categoryId ? null : categoryId,
    );
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductDrawerOpen(true);
  };

  const closeProductDrawer = () => {
    setProductDrawerOpen(false);
    setSelectedProduct(null);
  };

  const handleProductAddToCart = (cartItem) => {
    cartDrawerRef.current?.addItem(cartItem);
  };

  useEffect(() => {
    if (SEARCH_PLACEHOLDERS.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setSearchPlaceholderIndex(
        (currentIndex) => (currentIndex + 1) % SEARCH_PLACEHOLDERS.length,
      );
    }, SEARCH_PLACEHOLDER_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!outletId) {
      return;
    }

    if (outletId === outletData?._id) {
      return;
    }

    const nextOutlet = syncOutletData(outletId);
    setOutletData(nextOutlet);
    clearCachedMenu(outletData?._id);

    cartDrawerRef.current?.clearCart?.();
    clearLocalCart();
    setCart([]);

    setCategories([]);
    setSelectedCategoryId(null);
    setSelectedProduct(null);
    setProductDrawerOpen(false);
    setSearchQuery("");
    clearFilters();
  }, [outletId, outletData?._id]);

  useEffect(() => {
    if (!outletData?._id) {
      navigate("/", {
        replace: true,
      });

      return;
    }

    getCategoriesProductsList();
  }, [outletData?._id]);

  useLayoutEffect(() => {
    const header = stickyHeaderRef.current;

    if (!header) {
      return undefined;
    }

    const updateStickyHeaderHeight = () => {
      setStickyHeaderHeight(header.offsetHeight);
    };

    updateStickyHeaderHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateStickyHeaderHeight)
        : null;

    resizeObserver?.observe(header);

    window.addEventListener("resize", updateStickyHeaderHeight);

    return () => {
      window.removeEventListener("resize", updateStickyHeaderHeight);

      resizeObserver?.disconnect();
    };
  }, [loading, visibleCategories.length]);

  useEffect(() => {
    const container = categoryContainerRef.current;

    if (!container) {
      return undefined;
    }

    const handleCategoryUpdate = () => {
      window.requestAnimationFrame(updateRemainingCategoryCount);
    };

    handleCategoryUpdate();

    container.addEventListener("scroll", handleCategoryUpdate, {
      passive: true,
    });

    window.addEventListener("resize", handleCategoryUpdate);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(handleCategoryUpdate)
        : null;

    resizeObserver?.observe(container);

    return () => {
      container.removeEventListener("scroll", handleCategoryUpdate);

      window.removeEventListener("resize", handleCategoryUpdate);

      resizeObserver?.disconnect();
    };
  }, [visibleCategories, updateRemainingCategoryCount]);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        p: 0,
        m: 0,
        backgroundColor: "#DFEEF5",
        position: "relative",
        pb: 12,
        overflowX: "clip",
      }}
    >
      <Header outletData={outletData} canAnimate={canAnimate} />

      <Box
        component={motion.div}
        {...menuFadeUp(0.08, prefersReducedMotion, canAnimate)}
      >
        <AdvertisementCarousel outletData={outletData} orderType="PICKUP" />
      </Box>

      <TrackedOrders
        outletId={outletData?._id || outletId}
        canAnimate={canAnimate}
      />

      <Box
        ref={stickyHeaderRef}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backgroundColor: "#DFEEF5",
        }}
      >
        <Box
          component={motion.div}
          {...menuFadeUp(0.16, prefersReducedMotion, canAnimate)}
          sx={{
            px: 1.6,
            pt: 1.6,
            bgcolor: "#DFEEF5",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <TextField
              fullWidth
              value={searchQuery}
              placeholder={currentSearchPlaceholder.text}
              onChange={(event) => setSearchQuery(event.target.value)}
              inputProps={{
                "aria-label": "Search menu",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        display: "grid",
                        placeItems: "center",
                        overflow: "hidden",
                      }}
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        <Box
                          key={`${searchPlaceholderIndex}-${currentSearchPlaceholder.emoji}`}
                          component={motion.span}
                          initial={
                            prefersReducedMotion
                              ? false
                              : { y: 8, scale: 0.86 }
                          }
                          animate={{ y: 0, scale: 1 }}
                          exit={
                            prefersReducedMotion
                              ? undefined
                              : { y: -8, scale: 0.86 }
                          }
                          transition={
                            prefersReducedMotion
                              ? { duration: 0 }
                              : { duration: 0.28, ease: MENU_EASE }
                          }
                          aria-hidden
                          sx={{
                            fontSize: 20,
                            lineHeight: 1,
                          }}
                        >
                          {currentSearchPlaceholder.emoji}
                        </Box>
                      </AnimatePresence>
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{
                        color: "#09A5F7",
                        fontSize: 22,
                        mr: 0.4,
                      }}
                    />
                    <ProductFilterPopover
                      dietaryFilter={dietaryFilter}
                      sortOption={sortOption}
                      onDietaryFilterChange={setDietaryFilter}
                      onSortChange={setSortOption}
                      onClear={clearFilters}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 48,
                  borderRadius: "999px",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 8px 22px rgba(16,24,40,0.08)",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#12364D",
                  pr: 0.7,
                },

                "& input::placeholder": {
                  color: "transparent",
                  opacity: 0,
                },

                "& fieldset": {
                  border: "none",
                },
              }}
            />

            {!searchQuery && (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  left: 46,
                  right: 92,
                  top: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  pointerEvents: "none",
                  overflow: "hidden",
                }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <Box
                    key={`${searchPlaceholderIndex}-${currentSearchPlaceholder.text}`}
                    component={motion.span}
                    initial={
                      prefersReducedMotion ? false : { opacity: 0, y: 8 }
                    }
                    animate={{ opacity: 0.7, y: 0 }}
                    exit={
                      prefersReducedMotion
                        ? undefined
                        : { opacity: 0, y: -8 }
                    }
                    transition={
                      prefersReducedMotion
                        ? { duration: 0 }
                        : { duration: 0.28, ease: MENU_EASE }
                    }
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#12364D",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {currentSearchPlaceholder.text}
                  </Box>
                </AnimatePresence>
              </Box>
            )}
          </Box>
        </Box>

        {!loading && visibleCategories.length > 0 && (
          <Box
            sx={{
              pt: 1.8,
              pb: 0.4,
              backgroundColor: "#DFEEF5",
            }}
          >
          <Box
            component={motion.div}
            {...menuFadeUp(0.22, prefersReducedMotion, canAnimate)}
            sx={{
              position: "relative",
              backgroundColor: "#DFEEF5",
            }}
          >
            <Box
              ref={categoryContainerRef}
              onScroll={updateRemainingCategoryCount}
              sx={{
                display: "flex",
                gap: 1.35,
                overflowX: "auto",
                overflowY: "hidden",
                px: 2,
                pb: 1.2,
                scrollBehavior: "smooth",
                overscrollBehaviorX: "contain",

                "&::-webkit-scrollbar": {
                  display: "none",
                },

                scrollbarWidth: "none",
              }}
            >
              {visibleCategories.map((category, categoryIndex) => (
                <CategoryItem
                  key={category?._id}
                  index={categoryIndex}
                  prefersReducedMotion={prefersReducedMotion}
                  canAnimate={canAnimate}
                  name={category?.name}
                  image={category?.imageURL}
                  active={selectedCategoryId === category?._id}
                  onClick={() => handleCategoryClick(category?._id)}
                />
              ))}
            </Box>

            {remainingCategoryCount > 0 && (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    width: 52,
                    height: 74,
                    zIndex: 2,
                    pointerEvents: "none",
                    background:
                      "linear-gradient(90deg, rgba(223,238,245,0) 0%, rgba(223,238,245,0.55) 42%, #DFEEF5 100%)",
                  }}
                />

                <Box
                  component={motion.div}
                  initial={
                    prefersReducedMotion
                      ? false
                      : {
                          opacity: 0,
                          x: 10,
                        }
                  }
                  animate={
                    canAnimate || prefersReducedMotion
                      ? {
                          opacity: 1,
                          x: 0,
                        }
                      : {
                          opacity: 0,
                          x: 10,
                        }
                  }
                  transition={
                    prefersReducedMotion
                      ? { duration: 0 }
                      : {
                          duration: 0.35,
                          delay: canAnimate ? 0.35 : 0,
                          ease: MENU_EASE,
                        }
                  }
                  role="button"
                  tabIndex={0}
                  aria-label={`Show ${remainingCategoryCount} more categories`}
                  onClick={scrollToMoreCategories}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();

                      scrollToMoreCategories();
                    }
                  }}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: 10,
                    width: 32,
                    height: 46,
                    zIndex: 3,
                    cursor: "pointer",
                    borderRadius: "999px 0 0 999px",
                    backgroundColor: "#09A5F7",
                    border: "3px solid #FFE600",
                    borderRight: "none",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pl: 0.35,
                    boxShadow: "0 6px 14px rgba(9,165,247,0.28)",
                    transition:
                      "transform 150ms ease, background-color 150ms ease",

                    "&:hover": {
                      backgroundColor: "#078FD0",
                    },

                    "&:active": {
                      transform: "scale(0.97)",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      lineHeight: 1,
                      fontWeight: 800,
                      color: "#FFFFFF",
                      letterSpacing: 0.2,
                    }}
                  >
                    {remainingCategoryCount}+
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 1.6,
          pt: 1.1,
          backgroundColor: "#DFEEF5",
        }}
      >
        {loading ? (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{
              opacity: splashComplete ? 1 : 0,
            }}
            transition={{
              duration: prefersReducedMotion ? 0 : 0.3,
            }}
            sx={{
              minHeight: 300,
              display: "grid",
              placeItems: "center",
            }}
          >
            <CircularProgress
              sx={{
                color: "#09A5F7",
              }}
            />
          </Box>
        ) : visibleCategorySections.length === 0 ? (
          <Box
            component={motion.div}
            initial={
              prefersReducedMotion
                ? false
                : {
                    opacity: 0,
                    y: 14,
                  }
            }
            animate={
              canAnimate
                ? {
                    opacity: 1,
                    y: 0,
                  }
                : prefersReducedMotion
                  ? {
                      opacity: 1,
                      y: 0,
                    }
                  : {
                      opacity: 0,
                      y: 14,
                    }
            }
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : {
                    duration: 0.4,
                    ease: MENU_EASE,
                  }
            }
            sx={{
              textAlign: "center",
              py: 8,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 800,
                color: "#101828",
              }}
            >
              No products found
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 12.5,
                color: "#667085",
              }}
            >
              Try another category, filter or search term.
            </Typography>
          </Box>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${selectedCategoryId || "all"}-${dietaryFilter}-${sortOption}`}
              initial={
                prefersReducedMotion
                  ? false
                  : {
                      opacity: 0,
                    }
              }
              animate={
                canAnimate || prefersReducedMotion
                  ? {
                      opacity: 1,
                    }
                  : {
                      opacity: 0,
                    }
              }
              exit={
                prefersReducedMotion || !canAnimate
                  ? undefined
                  : {
                      opacity: 0,
                    }
              }
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : {
                      duration: 0.32,
                      ease: MENU_EASE,
                    }
              }
            >
              <Stack spacing={2.2}>
                {visibleCategorySections.map((category, sectionIndex) => {
                  const hasBanner = Boolean(category?.bannerImageUrl);

                  return (
                    <Box
                      key={category?._id}
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        borderRadius: "22px",
                        overflow: "visible",
                      }}
                    >
                    <CategoryStickyName
                      name={category?.name}
                      stickyTop={stickyHeaderHeight}
                      coveredByBanner={hasBanner}
                    />

                    {hasBanner ? (
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 3,
                          mt: `calc(-${CATEGORY_STICKY_NAME_HEIGHT}px - 1px)`,
                          borderRadius: "22px 22px 0 0",
                          overflow: "hidden",
                          lineHeight: 0,
                          transform: "translateZ(0)",
                        }}
                      >
                        <Box
                          component={motion.img}
                          initial={
                            prefersReducedMotion
                              ? false
                              : {
                                  opacity: 0,
                                }
                          }
                          animate={
                            canAnimate || prefersReducedMotion
                              ? {
                                  opacity: 1,
                                }
                              : {
                                  opacity: 0,
                                }
                          }
                          transition={
                            prefersReducedMotion
                              ? {
                                  duration: 0,
                                }
                              : {
                                  duration: 0.45,
                                  delay: canAnimate ? sectionIndex * 0.06 : 0,
                                  ease: MENU_EASE,
                                }
                          }
                          src={category.bannerImageUrl}
                          alt={category?.name || "Category"}
                          sx={{
                            display: "block",
                            width: "calc(100% + 2px)",
                            maxWidth: "none",
                            height: "auto",
                            ml: "-1px",
                            mt: "-1px",
                            mb: "-1px",
                            objectFit: "cover",
                            objectPosition: "center top",
                            border: 0,
                            outline: "none",
                            verticalAlign: "top",
                          }}
                        />
                      </Box>
                    ) : null}

                    <Box
                      sx={{
                        overflow: "hidden",
                        backgroundColor: "#FFFFFF",
                        borderTopLeftRadius: hasBanner ? 0 : "22px",
                        borderTopRightRadius: hasBanner ? 0 : "22px",
                        borderBottomLeftRadius: "22px",
                        borderBottomRightRadius: "22px",
                        ...(hasBanner
                          ? {}
                          : {
                              mt: `-${CATEGORY_STICKY_NAME_HEIGHT}px`,
                              pt: `${CATEGORY_STICKY_NAME_HEIGHT}px`,
                            }),
                      }}
                    >
                    {category?.products?.map((product, productIndex) => (
                      <Box
                        key={product?._id}
                        component={motion.div}
                        layout={!prefersReducedMotion}
                        initial={
                          prefersReducedMotion
                            ? false
                            : {
                                opacity: 0,
                                y: 16,
                              }
                        }
                        animate={
                          canAnimate
                            ? {
                                opacity: 1,
                                y: 0,
                              }
                            : prefersReducedMotion
                              ? {
                                  opacity: 1,
                                  y: 0,
                                }
                              : {
                                  opacity: 0,
                                  y: 16,
                                }
                        }
                        transition={
                          prefersReducedMotion
                            ? {
                                duration: 0,
                              }
                            : {
                                duration: 0.42,
                                delay: canAnimate
                                  ? sectionIndex * 0.05 + productIndex * 0.035
                                  : 0,
                                ease: MENU_EASE,
                              }
                        }
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          backgroundColor: "#FFFFFF",
                          borderBottom:
                            productIndex < category.products.length - 1
                              ? "2px dashed #D6E0E6"
                              : "none",
                        }}
                      >
                        <ProductCard
                          product={product}
                          image={getProductImage(product)}
                          price={getProductPrice(product)}
                          quantity={productQuantities[product?._id] || 0}
                          onSelect={() => handleProductSelect(product)}
                          onIncrease={() =>
                            cartDrawerRef.current?.increaseProduct?.(
                              product?._id,
                            )
                          }
                          onDecrease={() =>
                            cartDrawerRef.current?.decreaseProduct?.(
                              product?._id,
                            )
                          }
                        />
                      </Box>
                    ))}
                    </Box>
                  </Box>
                  );
                })}
              </Stack>
            </motion.div>
          </AnimatePresence>
        )}
      </Box>

      <CartDrawer
        key={outletId || outletData?._id}
        ref={cartDrawerRef}
        outletData={outletData}
        onMessage={showMessage}
        onCartChange={handleCartChange}
      />

      <ProductDrawer
        open={productDrawerOpen}
        product={selectedProduct}
        outletData={outletData}
        onClose={closeProductDrawer}
        onAddToCart={handleProductAddToCart}
        onMessage={showMessage}
      />

      <Snackbar
        open={message.open}
        autoHideDuration={2500}
        onClose={() =>
          setMessage((currentMessage) => ({
            ...currentMessage,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={message.severity}
          variant="filled"
          onClose={() =>
            setMessage((currentMessage) => ({
              ...currentMessage,
              open: false,
            }))
          }
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Menu;
